// -----------------------------------------------------------------------------
// BLOG POSTS - add new posts at the TOP of the array so newest appears first.
//
// Each post supports these content block types:
//   { type: 'paragraph', text: '...' }
//   { type: 'heading',   text: '...' }         <- renders as h3
//   { type: 'subheading', text: '...' }         <- renders as h4
//   { type: 'code',  language: 'js', text: '...' }
//   { type: 'list',  items: ['item 1', 'item 2'] }
//   { type: 'image', src: '/images/blog/x.jpg', alt: '...' }
//   { type: 'quote', text: '...', author: 'Optional' }
//   { type: 'divider' }
// -----------------------------------------------------------------------------

export const blogPosts = [
  {
    id: 'why-i-bet-on-cs',
    title: 'Why I Bet on Computer Science Before the Hiring Crash',
    date: '2026-05-10',
    excerpt: "Everyone was saying CS was dead. Layoffs, AI, oversaturation. I chose it anyway - not because it looked safe, but because I genuinely couldn't stop building things.",
    tags: ['career', 'cs', 'personal'],
    color: '#00A8E8',
    coverImage: '/images/blog/why_i_chose_cs_thumbnail.png',
    tldr: [
      'Spent my teen years gaming until it started feeling pointless',
      'Made $15k+ from Minecraft plugins - slowly, over years, just by consistently showing up',
      'COVID was prime time - no excuse not to build, so I just built',
      'Tried photography, art, robotics, math - programming was the only one that followed me through',
      'Chose CS during the hiring crash. I just couldn\'t stop coming back to it.',
    ],
    content: [
      {
        type: 'paragraph',
        text: "In my mid-teenage years I would spend quite some time playing video games. The thing is, every time I would finish a session, I would feel a bit empty. The game has a point, but never led to a bigger picture.",
      },
      {
        type: 'paragraph',
        text: "The games I played - Minecraft, Pixel Gun 3D, World of Tanks Blitz felt somewhat repetitive. They didn't bring any lasting impact besides memories I could reflect on later in life.",
      },
      {
        type: 'paragraph',
        text: "So things started feeling a bit pointless, I needed something more. Maybe we could try adding a little something to the gameplay?",
      },
      {
        type: 'heading',
        text: 'How Minecraft Changed Everything',
      },
      {
        type: 'paragraph',
        text: "When I was just running a Minecraft server for some friends, I tried Minecraft plugin development. I wanted to make something to track discoveries and teleport back to them, so we could speed up our game process and have more fun.",
      },
      {
        type: 'paragraph',
        text: "The mod wasn't anything fancy, matter of fact it was honestly terrible. For my dev friends: I didn't even know what OOP was, everything was public and static - no encapsulation, no abstraction, just something that somehow worked.",
      },
      // view it here: https://github.com/Kihsomray/CyberTravel/commit/98845f3abccc84e801c733a8ab487ef433b7b1c6
      {
        type: 'image',
        src: '/images/blog/ctp_public_static.png',
        alt: 'Early CyberTravel code, the discovery plugin.',
      },
      {
        type: 'paragraph',
        text: "Later, a friend was looking for a world resetting plugin for his server, and there was nothing good enough that he could find. So I made something, and he somehow actually liked it and used it.",
      },
      {
        type: 'paragraph',
        text: "I thought, why not toss it on a marketplace for free, maybe someone else will try it out?",
      },
      {
        type: 'paragraph',
        text: "At some point people started actually using my stuff. I remember refreshing player counts on servers running my plugins. Seeing the initial numbers grow felt like something else!",
      },
      {
        type: 'image',
        src: '/images/blog/clv_bstats.png',
        alt: 'CyberLevels stats. That levels plugin ended up in over 600 servers.',
      },
      {
        type: 'paragraph',
        text: "Server owners started messaging me. Then commissions from customizations. I was opening IntelliJ right after school, staying up until 2 or 3 AM debugging random stuff.",
      },
      {
        type: 'paragraph',
        text: "I remember spending hours trying to fix a levels plugin because the TPS kept dying whenever too many players were playing at once. At the time that felt like the most important problem in the world.",
      },
      {
        type: 'paragraph',
        text: "Over time I made a little over $15,000 from it. For something that started as me messing with a virtual block game with some friends, that genuinely felt kind of insane.",
      },
      {
        type: 'image',
        src: '/images/blog/minecraft_server.PNG',
        alt: 'Cool image from one of the servers I ran.',
      },
      {
        type: 'paragraph',
        text: "This is about the time coding went from being just a black box, to something I could kind of understand. And it made me excited - what else could I do with this stuff?",
      },
      {
        type: 'heading',
        text: 'COVID Was Prime Time',
      },
      {
        type: 'paragraph',
        text: "COVID hit, that was a very interesting time.",
      },
      {
        type: 'paragraph',
        text: "It felt like most of my friends and family were sleeping in or on TikTok all day. I thought that was pointless, so I kept polishing my coding skills and making more updates for the mods/plugins I had started.",
      },
      {
        type: 'paragraph',
        text: "It felt like I was spending an unhealthy amount of time on plugins. Trying to make better stuff. At that time I was probably spending more time on SpigotMC than on actual schoolwork.",
      },
      {
        type: 'paragraph',
        text: "Looking back, that period probably mattered more for how I work now than anything else. I had no excuse not to build, and it was fun. So I just built.",
      },
      {
        type: 'heading',
        text: 'The High School Impact',
      },
      {
        type: 'image',
        src: '/images/blog/KLHS_hallway.jpg',
        alt: 'KLHS hallways - they feel so familiar, like it was yesterday',
      },
      {
        type: 'paragraph',
        text: "High school was where I wanted to find out what I wanted to do long term. No one recommended this to me, just something I thought would be a good idea.",
      },
      {
        type: 'paragraph',
        text: "I got into photography for a while. Like actually into it, I bought my own camera using the money I made working at Fred Meyer. I also tried painting, robotics, 3D modeling, math, Java classes. Pretty much anything that let me build or create something.",
      },
      {
        type: 'paragraph',
        text: "To be fair, everything was pretty fun. Anything I chose at that time would've probably worked out, at least in terms of interest.",
      },
      {
        type: 'image',
        src: '/images/blog/KLHS_painting.JPG',
        alt: 'Something I painted for art class.',
      },
      {
        type: 'list',
        items: [
          "Art felt creative - but it probably wouldn't pay the bills",
          "Math felt satisfying - but I couldn't imagine pursuing it directly",
          "Robotics and 3D modeling felt hands-on - I almost chose real engineering because of this",
          "Photography felt expressive - I just kept it as a hobby",
        ],
      },
      {
        type: 'paragraph',
        text: "Programming just hit different. You could build and ship something so quickly, the sky was the limit.",
      },
      {
        type: 'heading',
        text: 'What Locked it in',
      },
      {
        type: 'paragraph',
        text: "The thing about programming that got me was that it rewarded obsession. The more time you put in, the better you got, there was essentially no ceiling.",
      },
      {
        type: 'paragraph',
        text: "It showed me the power of creation and automation. Simple code turns into complex code, and eventually you get a beautiful system that solves a problem and makes you some dough.",
      },
      {
        type: 'paragraph',
        text: "Also AI wasn't prominent during this time, so skill mattered. I did everything I could to develop my skill and push past the average Joe.",
      },
      {
        type: 'paragraph',
        text: "It just made sense, i'm a problem solver - this is a fun, practical, and fast-paced job that I am good at.",
      },
      {
        type: 'heading',
        text: 'On the Hiring Crash',
      },
      {
        type: 'paragraph',
        text: "The weird part is that by the time I actually committed to CS and was doing my program, everyone online was saying it was over.",
      },
      {
        type: 'paragraph',
        text: "Layoffs at Google, Meta, Amazon. Hiring freezes everywhere. Every other YouTube video was someone saying AI was replacing engineers and students were naive for going into it. Every forum thread looked the same: don't do it, oversaturated, you'll graduate with debt and zero offers.",
      },
      {
        type: 'image',
        src: '/images/blog/swe_job_trend.png',
        alt: 'Software dev job postings on Indeed, US. Source: FRED / St. Louis Fed',
      },
      {
        type: 'paragraph',
        text: "I chose it anyway - not because I had some plan or thought I was smarter than everyone warning against it. I just couldn't stop building things.",
      },
      {
        type: 'paragraph',
        text: "But on the other side of things, I don't think the doom was entirely wrong. The 2021-2022 hiring boom was obviously not sustainable and the correction followed. Entry-level positions dried up. Companies started expecting two years of experience for junior roles.",
      },
      {
        type: 'paragraph',
        text: "But I also think a lot of the panic was overcorrection. The demand for people who can actually build things didn't disappear. The bar just went higher - you HAVE to be good - at least at prompt engineering (lol).",
      },
      {
        type: 'paragraph',
        text: "That doesn't bother me. I'd rather compete in a market where skill matters than one where everyone with a basic certificate gets hired.",
      },
      {
        type: 'divider',
      },
      {
        type: 'paragraph',
        text: "So when people ask why I still chose CS during one of the worst hiring markets in years, the answer is pretty simple: I just couldn't stop coming back to it.",
      },
    ],
  },
  {
    id: 'hello-world',
    title: 'Hello World - Starting a Dev Blog',
    date: '2026-03-07',
    excerpt:
      'Every developer blog starts somewhere. Here\'s mine - why I\'m writing, what I\'ll cover, and what you can expect.',
    tags: ['general', 'intro'],
    color: '#00A8E8',
    coverImage: '',           // optional: '/images/blog/hello-world.jpg'
    content: [
      {
        type: 'paragraph',
        text: "I've been building software for a while now - full-stack apps, game mods, CAD tooling, Discord bots. But I've never really written about any of it. This blog is my attempt to change that.",
      },
      {
        type: 'heading',
        text: "What I'll Write About",
      },
      {
        type: 'list',
        items: [
          'Deep dives into projects I\'m working on',
          'Lessons learned the hard way',
          'Tools, libraries, and workflows I find interesting',
          'Thoughts on software engineering as a craft',
        ],
      },
      {
        type: 'paragraph',
        text: "I'm not trying to churn out SEO content or tutorial regurgitation. The goal is to write things I genuinely wish existed when I was figuring stuff out - concise, honest, and useful.",
      },
      {
        type: 'quote',
        text: 'Write to think. Publish to share the thinking.',
      },
      {
        type: 'paragraph',
        text: "If something here helps you or sparks a conversation, feel free to reach out. Let's build.",
      },
    ],
  },
];
